import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useDepartamento } from '../../contexts/DepartamentoContext';
import atividadeService from '../../services/atividadeService';
import usuarioService from '../../services/usuarioService';
import { Atividade, Usuario } from '../../types';
import { format } from 'date-fns';

// Tipos para os filtros
interface FiltrosAtividades {
  tipo?: string;
  usuario_origem_id?: string;
  data_inicio?: string;
  data_fim?: string;
}

const AtividadesDepartamento: React.FC = () => {
  const { usuario } = useAuth();
  const { ehLiderDepartamento } = useDepartamento();
  const [atividades, setAtividades] = useState<Atividade[]>([]);
  const [carregando, setCarregando] = useState<boolean>(true);
  const [erro, setErro] = useState<string>('');
  const [paginaAtual, setPaginaAtual] = useState<number>(1);
  const [totalPaginas, setTotalPaginas] = useState<number>(1);
  const [limite] = useState<number>(20);
  const [filtros, setFiltros] = useState<FiltrosAtividades>({});
  const [mostrarFiltrosAvancados, setMostrarFiltrosAvancados] = useState<boolean>(false);
  const [usuariosOrigem, setUsuariosOrigem] = useState<Usuario[]>([]);
  const [carregandoMais, setCarregandoMais] = useState<boolean>(false);

  // Carregar atividades do departamento
  const carregarAtividades = async (pagina: number = 1, limiteParam: number = limite, filtrosParam: FiltrosAtividades = filtros) => {
    if (!usuario) return;
    
    setCarregando(true);
    try {
      // Construir objeto de filtros para a API
      const filtrosAPI: any = { ...filtrosParam };
      
      // Adicionar filtros de data se existirem
      if (filtrosParam.data_inicio) {
        filtrosAPI.data_inicio = filtrosParam.data_inicio;
      }
      
      if (filtrosParam.data_fim) {
        filtrosAPI.data_fim = filtrosParam.data_fim;
      }
      
      const resposta = await atividadeService.obterTodas(
        filtrosAPI,
        { pagina, limite: limiteParam }
      );
      
      if (resposta.success && resposta.atividades) {
        const novasAtividades = resposta.atividades || [];
        if (pagina === 1) {
          setAtividades(novasAtividades);
        } else {
          setAtividades(prev => [...prev, ...novasAtividades]);
        }
        
        if (resposta.paginacao) {
          setPaginaAtual(resposta.paginacao.pagina);
          setTotalPaginas(resposta.paginacao.paginas);
        }
        
        setErro('');
      } else {
        throw new Error(resposta.message || 'Erro ao carregar atividades');
      }
    } catch (error: any) {
      setErro(error.message || 'Erro ao carregar atividades');
      console.error('Erro ao carregar atividades:', error);
    } finally {
      setCarregando(false);
    }
  };

  // Carregar mais atividades (paginação)
  const carregarMaisAtividades = async () => {
    if (paginaAtual >= totalPaginas || carregando) return;
    
    setCarregandoMais(true);
    const proximaPagina = paginaAtual + 1;
    await carregarAtividades(proximaPagina);
    setCarregandoMais(false);
  };

  // Carregar usuários para o filtro
  const carregarUsuarios = async () => {
    try {
      const resposta = await usuarioService.obterParaDropdown();
      if (resposta.success && resposta.usuarios) {
        setUsuariosOrigem(resposta.usuarios);
      } else {
        throw new Error(resposta.message || 'Erro ao carregar usuários');
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    }
  };

  // Efeito para carregar atividades quando o componente montar
  useEffect(() => {
    if (usuario && (ehLiderDepartamento || usuario.eh_lider || usuario.tipo === 'admin')) {
      carregarAtividades();
      carregarUsuarios();
    }
  }, [usuario, ehLiderDepartamento]);

  // Função para aplicar filtros
  const aplicarFiltros = () => {
    carregarAtividades(1, limite, filtros);
  };

  // Função para limpar filtros
  const limparFiltros = () => {
    setFiltros({});
    carregarAtividades(1, limite, {});
  };

  // Função para formatar data/hora
  const formatarDataHora = (data_hora: string) => {
    try {
      return format(new Date(data_hora), "dd/MM/yyyy 'às' HH:mm");
    } catch (error) {
      return data_hora;
    }
  };

  // Função para obter ícone baseado no tipo de atividade
  const getIconeAtividade = (tipo: string) => {
    switch (tipo) {
      case 'nova_tarefa':
        return <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>;
      case 'comentario':
        return <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>;
      case 'mudanca_status':
        return <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>;
      case 'mencao':
        return <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" /></svg>;
      case 'prazo_proximo':
        return <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
      case 'aprovacao_solicitada':
        return <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
      default:
        return <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>;
    }
  };

  // Verificar se o usuário tem permissão para acessar esta página
  if (!usuario || (!ehLiderDepartamento && !usuario.eh_lider && usuario.tipo !== 'admin')) {
    return (
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <div className="text-center py-8">
          <svg className="mx-auto h-12 w-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">Acesso Restrito</h3>
          <p className="mt-1 text-sm text-gray-500">
            Esta página é exclusiva para líderes de departamento e administradores.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Atividades do Departamento</h2>
        <div className="flex space-x-2">
          <button
            onClick={() => setMostrarFiltrosAvancados(!mostrarFiltrosAvancados)}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
          >
            {mostrarFiltrosAvancados ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          </button>
        </div>
      </div>

      {/* Filtros avançados */}
      {mostrarFiltrosAvancados && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Atividade</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={filtros.tipo || ''}
                onChange={(e) => setFiltros({ ...filtros, tipo: e.target.value || undefined })}
              >
                <option value="">Todos</option>
                <option value="nova_tarefa">Nova Tarefa</option>
                <option value="comentario">Comentário</option>
                <option value="mudanca_status">Mudança de Status</option>
                <option value="mencao">Menção</option>
                <option value="prazo_proximo">Prazo Próximo</option>
                <option value="aprovacao_solicitada">Aprovação Solicitada</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Usuário</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={filtros.usuario_origem_id || ''}
                onChange={(e) => setFiltros({ ...filtros, usuario_origem_id: e.target.value || undefined })}
              >
                <option value="">Todos</option>
                {usuariosOrigem.map(user => (
                  <option key={user.id} value={user.id}>{user.nome}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Início</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={filtros.data_inicio || ''}
                onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value || undefined })}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                value={filtros.data_fim || ''}
                onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value || undefined })}
              />
            </div>
          </div>
          
          <div className="mt-4 flex justify-end space-x-2">
            <button
              onClick={limparFiltros}
              className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
            >
              Limpar
            </button>
            <button
              onClick={aplicarFiltros}
              className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
            >
              Aplicar Filtros
            </button>
          </div>
        </div>
      )}

      {carregando && atividades.length === 0 ? (
        <div className="flex justify-center py-8">
          <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      ) : (
        <div className="space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto">
          {atividades.length === 0 ? (
            <p className="text-gray-500 text-center py-4">Nenhuma atividade encontrada</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tipo
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Título
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tarefa
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Usuário
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data/Hora
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {atividades.map((atividade) => (
                      <tr key={atividade.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getIconeAtividade(atividade.tipo)}
                            <span className="ml-2 text-sm text-gray-900">
                              {atividade.tipo === 'nova_tarefa' && 'Nova Tarefa'}
                              {atividade.tipo === 'comentario' && 'Comentário'}
                              {atividade.tipo === 'mudanca_status' && 'Mudança de Status'}
                              {atividade.tipo === 'mencao' && 'Menção'}
                              {atividade.tipo === 'prazo_proximo' && 'Prazo Próximo'}
                              {atividade.tipo === 'aprovacao_solicitada' && 'Aprovação Solicitada'}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{atividade.titulo}</div>
                          {atividade.descricao && (
                            <div className="text-sm text-gray-500 truncate max-w-xs">{atividade.descricao}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{atividade.tarefa_titulo}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {atividade.usuario_origem_avatar ? (
                              <img 
                                className="h-8 w-8 rounded-full mr-2" 
                                src={atividade.usuario_origem_avatar} 
                                alt={atividade.usuario_origem_nome || 'Avatar'} 
                              />
                            ) : (
                              <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center mr-2">
                                <svg className="h-4 w-4 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                            <div className="text-sm font-medium text-gray-900">
                              {atividade.usuario_origem_nome || 'Usuário'}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatarDataHora(atividade.data_hora)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Link
                            to={`/tarefas/${atividade.tarefa_id}`}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Ver Tarefa
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Botão para carregar mais atividades */}
              {paginaAtual < totalPaginas && (
                <div className="mt-4 text-center">
                  <button
                    onClick={carregarMaisAtividades}
                    className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-colors"
                    disabled={carregandoMais}
                  >
                    {carregandoMais ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-700 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Carregando...
                      </>
                    ) : (
                      'Carregar mais'
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AtividadesDepartamento;
